import type { SeatingPrintOptions } from '@app/shared'

/**
 * URL for the printed sheet. Same-origin `/api` so the dev proxy and the
 * deployed origin both work without a base-URL constant.
 *
 * The PDF is rendered by the API from the SAME resolved occasion the page is
 * showing — it is not a second computation of the times.
 */
export function pdfUrl(params: {
  spaceId: string
  occasionKey: string
  from: string
  /** Carry the English gloss under each label onto the printed sheet. */
  showSubtitle?: boolean
  /** Print the civil date beside the Hebrew one. */
  showCivilDate?: boolean
  /** How the masthead closes itself off: its form, its rule, its ink. */
  /** A `LETTERHEAD_PRESETS` key. Omitted prints the shul's own stationery. */
  letterhead?: string
  /**
   * The reasoning sheet instead of the schedule: the rule behind each time,
   * flowing to as many pages as it takes rather than fitted to one.
   *
   * Unlike everything else here it needs a session — the schedule is public,
   * the shul's account of why is not.
   */
  reasoning?: boolean
  /** Whether the reasoning says how far it is to be trusted. On by default. */
  showConfidence?: boolean
}): string {
  const {
    showSubtitle,
    showCivilDate,
    letterhead,
    reasoning,
    showConfidence,
    ...rest
  } = params
  const q = new URLSearchParams(rest)
  // These default off, so only the ON case needs carrying.
  if (showSubtitle) q.set('showSubtitle', '1')
  if (showCivilDate) q.set('showCivilDate', '1')
  if (letterhead) q.set('letterhead', letterhead)
  if (reasoning) q.set('reasoning', '1')
  // The one that defaults ON, so it is the OFF case that travels.
  if (reasoning && showConfidence === false) q.set('showConfidence', '0')
  return `/api/pdf/occasion?${q}`
}

/**
 * URL for the aliyos roster. Authenticated, unlike the occasion sheet: it is
 * the member roster, so it is opened in a tab rather than fetched — the browser
 * carries the session cookie and the PDF viewer takes it from there.
 */
export function aliyaRosterUrl(spaceId: string): string {
  return `/api/pdf/aliya-roster?spaceId=${encodeURIComponent(spaceId)}`
}

/** URL for the private standing seating chart. */
export function seatingChartUrl(
  spaceId: string,
  options: SeatingPrintOptions,
): string {
  const query = new URLSearchParams({ spaceId, paper: options.paper })
  for (const [roomId, show] of Object.entries(options.show)) {
    query.set(`show_${roomId}`, show)
  }
  return `/api/pdf/seating-chart?${query.toString()}`
}

/** URL for a gift's receipt, as the office prints it. */
export function giftReceiptUrl(spaceId: string, donationId: string): string {
  const query = new URLSearchParams({ spaceId, donationId })
  return `/api/pdf/gift-receipt?${query.toString()}`
}

/** URL for a campaign year's gifts as a workbook, a column per question. */
export function campaignGiftsExportUrl(
  spaceId: string,
  campaignId: string,
): string {
  const query = new URLSearchParams({ spaceId, campaignId })
  return `/api/export/campaign-gifts.xlsx?${query.toString()}`
}

/** URL for one household's family correction sheet. */
export function familyCheckSheetUrl(
  spaceId: string,
  householdId: string,
  include: { contacts: boolean; address: boolean },
): string {
  const query = new URLSearchParams({ spaceId, householdId })
  if (include.contacts) query.set('contacts', '1')
  if (include.address) query.set('address', '1')
  return `/api/pdf/family-check-sheet?${query.toString()}`
}

/**
 * URL for a book as a PDF, keyed on the book like everything a reader
 * reaches — a book lives in its owner's own space, which a sharee cannot name.
 *
 * Opened rather than fetched, so the browser carries the session and the whole
 * book never passes through the page's memory to be handed back.
 */
export function bookPdfUrl(bookId: string, includeCover = true): string {
  const query = new URLSearchParams({ bookId })
  if (!includeCover) query.set('cover', '0')
  return `/api/studio/book.pdf?${query}`
}

/**
 * The same book from a public link, where the token is the whole authorization
 * and there is no book id to ask with — a published copy names nothing but
 * itself.
 *
 * What comes back is the PUBLISHED copy: it leaves out whatever the page leaves
 * out, so the license cannot be stepped around by asking for the PDF instead.
 */
export function sharedBookPdfUrl(token: string, includeCover = true): string {
  const query = new URLSearchParams({ token })
  if (!includeCover) query.set('cover', '0')
  return `/api/studio/book.pdf?${query}`
}

/**
 * URL for the members spreadsheet. Authenticated like the roster, and opened
 * rather than fetched so the browser downloads it with the session it has.
 */
export function membersExportUrl(params: {
  spaceId: string
  grain: string
  columns: string[]
  status?: string
  kind?: string
  type?: string
  search?: string
}): string {
  const q = new URLSearchParams({
    spaceId: params.spaceId,
    grain: params.grain,
    columns: params.columns.join(','),
  })
  if (params.status) q.set('status', params.status)
  if (params.kind) q.set('kind', params.kind)
  if (params.type) q.set('type', params.type)
  if (params.search) q.set('search', params.search)
  return `/api/export/members.xlsx?${q.toString()}`
}

/**
 * A year of Shabbosim as a PDF.
 *
 * Same-origin so the dev proxy and production both work, and opened with
 * `window.open` so the browser carries the session — though this route is
 * public, like the occasion sheet, because every cell is a published time.
 */
export function yearTableUrl({
  spaceId,
  year,
  preset,
  columns,
  orientation,
  showSubtitle,
}: {
  spaceId: string
  year: number
  /** A shipped set by name. The server owns what a preset means. */
  preset?: string
  /** Explicit column ids, when the caller has its own set. */
  columns?: string[]
  orientation?: 'portrait' | 'landscape'
  showSubtitle?: boolean
}): string {
  const q = new URLSearchParams({ spaceId, year: String(year) })
  if (preset) q.set('preset', preset)
  if (columns?.length) q.set('columns', columns.join(','))
  if (orientation) q.set('orientation', orientation)
  if (showSubtitle) q.set('showSubtitle', '1')
  return `/api/pdf/year-table?${q.toString()}`
}
